/**
 * LessonCompleteOverlay
 *
 * Inline confirmation panel that appears over the lesson viewer the
 * moment a lesson finishes (video ended, doc viewed, quiz passed,
 * etc.). The lesson content stays visible but dimmed so the learner
 * gets a calm, unmistakable signal that they've finished — and a one-
 * click path to the next lesson.
 *
 * Renders an "end of course" variant when there's no next lesson so
 * the last lesson doesn't leave the learner stranded.
 *
 * Portaled to document.body and fixed to the viewport rather than
 * scoped inside the lesson viewer: the viewer clips to its content with
 * overflow:hidden, so a short lesson (e.g. a one-paragraph text lesson)
 * would cut the card off top and bottom. Rendering outside that box
 * keeps the card fully visible whatever the lesson's height.
 */

import { createPortal } from 'react-dom';
import { Box, Button, IconButton, Typography } from '../ui';
import { CheckCircleIcon, ArrowForwardIcon, CloseIcon } from '../icons';

interface LessonCompleteOverlayProps {
  lessonTitle: string;
  /** When provided, an "advance to next" CTA renders. Pass null/undefined
   *  if the completed lesson was the last one in the curriculum. */
  nextLesson: { id: number | string; title: string } | null;
  onAdvance: () => void;
  onDismiss: () => void;
}

export default function LessonCompleteOverlay({
  lessonTitle,
  nextLesson,
  onAdvance,
  onDismiss,
}: LessonCompleteOverlayProps) {
  return createPortal(
    <Box
      sx={{
        // Fixed to the viewport (portaled to body) so the card is never
        // clipped by the lesson viewer's overflow:hidden. zIndex sits at
        // the MUI modal layer so it clears the navbar and sidebar.
        position: 'fixed',
        inset: 0,
        zIndex: 1300,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        // Semi-opaque light backdrop + subtle blur fades the lesson
        // underneath without hiding it entirely — the learner still
        // perceives "I was here" while the overlay takes focus.
        bgcolor: 'rgba(255,255,255,0.82)',
        backdropFilter: 'blur(4px)',
        WebkitBackdropFilter: 'blur(4px)',
        animation: 'lmsCompleteFade 280ms ease-out',
        '@keyframes lmsCompleteFade': {
          from: { opacity: 0 },
          to: { opacity: 1 },
        },
      }}
    >
      <Box
        sx={{
          position: 'relative',
          maxWidth: 480,
          width: 'calc(100% - 48px)',
          bgcolor: 'background.paper',
          borderRadius: 3,
          border: '1px solid',
          borderColor: 'rgba(46,125,50,0.30)',
          boxShadow: '0 24px 60px rgba(0,0,0,0.18)',
          p: { xs: 3.5, sm: 5 },
          textAlign: 'center',
          animation: 'lmsCompletePop 320ms cubic-bezier(0.2, 0.9, 0.3, 1.2)',
          '@keyframes lmsCompletePop': {
            from: { opacity: 0, transform: 'scale(0.94)' },
            to: { opacity: 1, transform: 'scale(1)' },
          },
        }}
      >
        {/* Close — for learners who want to linger on the content
            (e.g., scrub back through the video). Doesn't undo the
            completion, just hides this overlay. */}
        <IconButton
          size="small"
          onClick={onDismiss}
          aria-label="Dismiss completion message"
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
            color: 'text.secondary',
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>

        <CheckCircleIcon
          sx={{
            fontSize: 64,
            color: 'success.main',
            mb: 1.5,
            display: 'block',
            mx: 'auto',
          }}
        />

        <Typography variant="h5" fontWeight={700} sx={{ mb: 0.75 }}>
          Lesson complete!
        </Typography>

        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ mb: 3, px: { sm: 1 } }}
        >
          {nextLesson
            ? `You finished "${lessonTitle}". Ready for the next one?`
            : `You finished "${lessonTitle}" — and the rest of the course. Nice work.`}
        </Typography>

        {nextLesson ? (
          <Button
            variant="contained"
            color="primary"
            endIcon={<ArrowForwardIcon />}
            onClick={onAdvance}
            fullWidth
            sx={{
              color: '#fff',
              py: 1.25,
              textTransform: 'none',
              fontWeight: 700,
              fontSize: '0.95rem',
              mb: 1,
            }}
          >
            <Box
              component="span"
              sx={{
                display: 'inline-block',
                maxWidth: 320,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              Next: {nextLesson.title}
            </Box>
          </Button>
        ) : (
          <Typography
            variant="body2"
            sx={{
              color: 'success.dark',
              fontWeight: 600,
              mb: 1,
            }}
          >
            🎉 Course complete
          </Typography>
        )}

        <Button
          variant="text"
          color="inherit"
          onClick={onDismiss}
          sx={{ textTransform: 'none', fontSize: '0.85rem' }}
        >
          Stay on this lesson
        </Button>
      </Box>
    </Box>,
    document.body,
  );
}
